import { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { requestTyped } from '@xdocs/ws-protocol';
import { adminUserListQuery } from '@xdocs/schemas';
import type { AdminUserListResult, ErrorAlert, UserPublic, UserRole, UserStatus, UserTier } from '@xdocs/schemas';
import type { DataTableQuery } from '@xdocs/ui';
import { useWs } from '../../ws/WsProvider.js';

export interface AdminUsersFilters {
  role?: UserRole;
  tier?: UserTier;
  status?: UserStatus;
}

const DEFAULT_QUERY: DataTableQuery = { page: 1, pageSize: 25, sort: 'createdAt', dir: 'desc' };

export interface UseAdminUsersListResult {
  rows: readonly UserPublic[];
  total: number | null;
  nextCursor: string | null;
  query: DataTableQuery;
  filters: AdminUsersFilters;
  onQueryChange: (next: DataTableQuery) => void;
  onFiltersChange: (next: AdminUsersFilters) => void;
  onLoadMore: () => void;
  isLoading: boolean;
  isLoadingMore: boolean;
  isError: boolean;
  loadMoreError: string | undefined;
  onRetry: () => void;
  invalidate: () => void;
}

/**
 * Server-side pagination/sort/search/filter over `admin.users.list` (F54's
 * `<DataTable>`), the same `<DataTable>`-bridging shape `useSharesList.ts`
 * established (E4 §8.3). Filters (`role`/`tier`/`status`) are separate
 * client state from `DataTableQuery` because they're admin-specific facets
 * the shared `<DataTable>` toolbar doesn't render — the page composes its
 * own `<Select>` filter row alongside the table.
 */
export function useAdminUsersList(): UseAdminUsersListResult {
  const { wsClient, connectionState } = useWs();
  const queryClient = useQueryClient();
  const [query, setQuery] = useState<DataTableQuery>(DEFAULT_QUERY);
  const [filters, setFilters] = useState<AdminUsersFilters>({});
  const [rows, setRows] = useState<UserPublic[]>([]);
  const appendModeRef = useRef(false);
  const enabled = connectionState === 'connected';
  const queryKey = ['admin', 'users', 'list', query, filters] as const;

  const result = useQuery<AdminUserListResult, ErrorAlert>({
    queryKey,
    queryFn: () => requestTyped(wsClient, 'admin.users.list', adminUserListQuery.parse({ ...query, ...filters })),
    enabled,
  });

  useEffect(() => {
    if (!result.data) return;
    setRows((prev) => (appendModeRef.current ? [...prev, ...result.data.rows] : result.data.rows));
  }, [result.data]);

  const onQueryChange = useCallback((next: DataTableQuery) => {
    appendModeRef.current = false;
    setQuery(next);
  }, []);

  const onFiltersChange = useCallback((next: AdminUsersFilters) => {
    appendModeRef.current = false;
    setFilters(next);
    setQuery((q) => ({ ...q, page: 1 }));
  }, []);

  const onLoadMore = useCallback(() => {
    appendModeRef.current = true;
    setQuery((q) => ({ ...q, page: q.page + 1 }));
  }, []);

  const onRetry = useCallback(() => void result.refetch(), [result]);
  const invalidate = useCallback(() => void queryClient.invalidateQueries({ queryKey: ['admin', 'users', 'list'] }), [queryClient]);

  const total = result.data?.total ?? null;
  const hasMore = total !== null ? rows.length < total : false;
  const isAppendFetch = appendModeRef.current && result.isFetching;

  return {
    rows,
    total,
    nextCursor: hasMore ? 'more' : null,
    query,
    filters,
    onQueryChange,
    onFiltersChange,
    onLoadMore,
    isLoading: result.isLoading && !appendModeRef.current,
    isLoadingMore: isAppendFetch,
    isError: result.isError,
    loadMoreError: appendModeRef.current && result.isError ? result.error?.message : undefined,
    onRetry,
    invalidate,
  };
}
